import React, { useEffect, useState } from "react";
import { Heart, Phone, Copy, PaperPlaneRight, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { AutoGrowTextarea } from "@/components/AutoGrowTextarea";

const isPhone = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export const SosButton = ({ variant = "header", open: controlledOpen, onOpenChange }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [openState, setOpenState] = useState(false);
  const open = onOpenChange ? !!controlledOpen : openState;
  const setOpen = onOpenChange || setOpenState;
  const [res, setRes] = useState(null);
  const [step, setStep] = useState("resources"); // resources | compose | sent
  const [body, setBody] = useState("");
  const [ghost, setGhost] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/sos/resources").then(r => setRes(r.data)).catch(() => {});
  }, [user?.id]); // eslint-disable-line

  if (!user || !res || !res.sos_enabled) return null;
  if (user.role === "public_student") return null;
  const phone = isPhone();
  const isSchoolStudent = user.role === "anonymous_token_student";

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post("/sos/send", { body: body.trim(), ghost_mode: ghost });
      setStep("sent");
      setBody("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const copyNum = (n) => {
    navigator.clipboard?.writeText(n).then(() => toast.success(t("Number copied — pick up a phone and dial it")));
  };

  return (
    <>
      {variant === "menu" ? null : variant === "lesson" ? (
        <button data-testid="lesson-helpline-button"
          className="btn-ghost text-xs font-semibold flex items-center gap-1.5 text-[#000000] border border-[#FCA5A5] rounded-full px-3 py-1.5 bg-white hover:bg-[#FEF2F2] cursor-pointer"
          onClick={() => { setStep("resources"); setOpen(true); }}>
          <Heart size={15} weight="fill" color="#DC2626" /> {t("Help Line Numbers")}
        </button>
      ) : (
        <button data-testid="sos-button"
          className="relative flex items-center px-2.5 py-1.5 rounded-full bg-white border border-[#FCA5A5] hover:bg-[#FEF2F2] transition-colors cursor-pointer"
          title="Help / SOS" onClick={() => { setStep("resources"); setOpen(true); }}>
          <Heart size={18} weight="fill" color="#DC2626" />
        </button>
      )}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setStep("resources"); }}>
        <DialogContent className="max-w-md bg-white" aria-describedby={undefined} data-testid="sos-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Heart size={20} weight="fill" color="#DC2626" /> {t("Help & Support")}
            </DialogTitle>
          </DialogHeader>

          {step === "resources" && (
            <div className="space-y-3" data-testid="sos-resources">
              {!phone && (
                <div className="rounded-xl bg-[#FEF2F2] border border-[#FCA5A5] p-3 text-sm text-[#991B1B] font-semibold" data-testid="sos-no-telephony-banner">
                  {t("You're on a device that can't make calls. Pick up a phone and dial the number you need.")}
                </div>
              )}
              <div className="text-xs uppercase tracking-[0.15em] text-[#000000]">{res.region} — {t("crisis resources")}</div>
              <div className="flex flex-col gap-2">
                {res.hotlines.map((h, i) => (
                  phone ? (
                    <a key={i} href={`tel:${h.number.replace(/[^0-9+]/g, "")}`} data-testid={`sos-hotline-${i}`}
                      className="flex items-center justify-between gap-x-3 flex-wrap px-4 py-3 rounded-xl border border-[#C4C8CE] bg-[#F9FAFB] hover:border-[#DC2626] no-underline">
                      <span className="font-semibold text-[#000000]">{t(h.label)}</span>
                      <span className="flex items-center gap-1.5 text-[#DC2626] font-bold"><Phone size={16} weight="fill" /> {h.number}</span>
                    </a>
                  ) : (
                    <button key={i} data-testid={`sos-hotline-${i}`} onClick={() => copyNum(h.number)}
                      className="flex items-center justify-between gap-x-3 flex-wrap px-4 py-3 rounded-xl border border-[#C4C8CE] bg-[#F9FAFB] hover:border-[#DC2626] cursor-pointer">
                      <span className="font-semibold text-[#000000]">{t(h.label)}</span>
                      <span className="flex items-center gap-1.5 text-[#DC2626] font-extrabold text-lg">{h.number} <Copy size={15} /></span>
                    </button>
                  )
                ))}
              </div>
              <button data-testid="sos-open-compose" className="btn-primary w-full flex items-center justify-center gap-2"
                onClick={() => setStep("compose")}>
                <PaperPlaneRight size={16} weight="bold" /> {t("Send a private message to a confidential mentor")}
              </button>
            </div>
          )}

          {step === "compose" && (
            <div className="space-y-3" data-testid="sos-compose">
              <button className="btn-ghost text-xs flex items-center gap-1 text-[#000000]" onClick={() => setStep("resources")}>
                <ArrowLeft size={13} /> {t("Back to resources")}
              </button>
              <p className="text-sm text-[#000000]">
                {t("Your message goes privately to a trusted mentor. Honest note: if you mention being hurt or in danger, the law requires the mentor to get you help — that's a good thing. If this is an emergency, call 911 now.")}
              </p>
              <AutoGrowTextarea data-testid="sos-message-input" value={body} onChange={e => setBody(e.target.value)}
                minRows={2} maxHeight={200} placeholder={t("What's going on? Take your time…")}
                className="w-full rounded-xl border border-[#C4C8CE] p-3 text-sm bg-white" />
              {isSchoolStudent && (
                <div className="flex items-start gap-3 rounded-xl border border-[#C4C8CE] bg-[#F9FAFB] p-3">
                  <Switch data-testid="sos-ghost-toggle" checked={ghost} onCheckedChange={setGhost} />
                  <div className="text-xs text-[#000000]">
                    <div className="font-bold text-[#000000]">{t("I want to stay completely anonymous. The mentor won't know who I am.")}</div>
                    {ghost && (
                      <div className="mt-1.5 text-[#166534]" data-testid="sos-ghost-notice">
                        {t("🟢 Ghost Mode is ON. The mentor will see only \"Anonymous student at your school\" — no first name, no ID. Reminder: if your message mentions specific people, places, or class details, that can still identify you. Share only what you're comfortable sharing.")}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <button data-testid="sos-send-button" className="btn-primary w-full" onClick={send}
                disabled={sending || !body.trim()}>
                {sending ? t("Sending…") : t("Send private message")}
              </button>
            </div>
          )}

          {step === "sent" && (
            <div className="text-center py-6 space-y-3" data-testid="sos-sent-confirmation">
              <CheckCircle size={44} weight="fill" color="#16A34A" className="mx-auto" />
              <div className="font-bold font-display text-lg">{t("Message sent")}</div>
              <p className="text-sm text-[#000000]">{t("A mentor will reply in your Inbox (the chat icon at the top of the page). You're not alone.")}</p>
              <button className="btn-secondary" onClick={() => setOpen(false)}>{t("Close")}</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
